import { LogoFileMimeType } from 'libs/end-to-end/src/logos';
import { UploadStatus } from 'libs/end-to-end/src/upload';
import { z } from 'zod';

/**
 * Schema for validating organization data
 * @remarks
 * Defines the core properties of an organization including its identifier,
 * name, and optional logo MIME type
 */
export const Organization = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  logoMimeType: LogoFileMimeType.optional(),
  logoUrl: z.string().optional(),
});
// org_user, org_admin -> claim name: xyz claim value: def
export type Organization = z.infer<typeof Organization>;

/**
 * Schema for organization upload entries
 * @remarks
 * Represents an upload associated with an organization, including its status
 * and visibility settings
 */
export const OrganizationUpload = z.object({
  uploadId: z.string().min(1),
  name: z.string().min(1),
  status: UploadStatus,
  isHidden: z.boolean().optional(),
});
export type OrganizationUpload = z.infer<typeof OrganizationUpload>;

/**
 * Schema for a list of organization uploads
 * @remarks
 * Contains an array of OrganizationUpload entries
 */
export const OrganizationUploads = z.array(OrganizationUpload);
export type OrganizationUploads = z.infer<typeof OrganizationUploads>;

/**
 * Schema for field mapping configuration metadata
 * @remarks
 * Defines the basic identifying information for a field mapping configuration
 */
export const FieldMappingConfigMeta = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
});
export type FieldMappingConfigMeta = z.infer<typeof FieldMappingConfigMeta>;

/**
 * Schema for field mapping configuration index
 * @remarks
 * Contains an array of field mapping configuration metadata entries
 */
export const FieldMappingConfigIndex = z.object({
  metas: z.array(FieldMappingConfigMeta),
});
export type FieldMappingConfigIndex = z.infer<typeof FieldMappingConfigIndex>;

/**
 * Schema for creating or updating an organization
 * @remarks
 * Omits the id field from the Organization schema as it's not required
 * for creation/update operations
 */
export const CreateOrUpdateOrganizationRequestBodySchema = Organization.omit({
  id: true,
});
export type CreateOrUpdateOrganizationRequestBodyType = z.infer<
  typeof CreateOrUpdateOrganizationRequestBodySchema
>;

/**
 * Schema for organization logo upload URL response
 * @remarks
 * Contains the presigned URL for uploading an organization's logo
 */
export const OrganizationLogoUploadUrl = z.object({
  url: z.string(),
});
export type OrganizationLogoUploadUrl = z.infer<
  typeof OrganizationLogoUploadUrl
>;
